import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchProducts, fetchFeaturedProducts, fetchNewProducts } from '@/lib/api';
import { Product } from '@/lib/types';

interface ProductContextType {
  refreshProducts: () => Promise<void>;
  isSyncing: boolean;
  lastSyncTime: Date | null;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};

export const ProductProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const queryClient = useQueryClient();

  // Sincronização dos produtos
  const refreshProducts = async () => {
    setIsSyncing(true);
    try {
      // Atualiza o cache para todas as queries relacionadas a produtos
      await Promise.all([
        queryClient.prefetchQuery({
          queryKey: ['products'],
          queryFn: () => fetchProducts({})
        }),
        queryClient.prefetchQuery({
          queryKey: ['products', 'featured'],
          queryFn: () => fetchFeaturedProducts()
        }),
        queryClient.prefetchQuery({
          queryKey: ['products', 'new'],
          queryFn: () => fetchNewProducts()
        })
      ]);
      
      setLastSyncTime(new Date());
    } catch (error) {
      console.error('Erro ao sincronizar produtos:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  // Sincroniza produtos ao montar o componente
  useEffect(() => {
    refreshProducts();
    
    // Sincroniza a cada 5 minutos (opcional)
    const interval = setInterval(() => {
      refreshProducts();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <ProductContext.Provider value={{ refreshProducts, isSyncing, lastSyncTime }}>
      {children}
    </ProductContext.Provider>
  );
};

export default ProductProvider; 